/* Xiaomi 2026 · Ayush · "Edit options" modal
 *
 * Single dialog showing ALL six fields (3 prefs_2026 + 3 history_2025)
 * for one partner. Save All sends ONE PATCH to /api/xiaomi/dealers/:id
 * with all dotted-path fields → atomic update, single audit log entry
 * covering every changed field.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'x-edit-source': 'inline',
  },
});

// Mapping: row's flat key → dealer doc's dotted path
const FIELDS = [
  { rowKey: 'dinner_4star',                 path: 'preferences_2026.dinner_4star',                 label: '2026 · Dinner choices (4★)',          group: 'plan' },
  { rowKey: 'weekend_anniversary_dinner',   path: 'preferences_2026.weekend_anniversary_dinner',   label: '2026 · Weekend / anniversary dinner', group: 'plan', highlight: true },
  { rowKey: 'holiday_ooty_first',           path: 'preferences_2026.holiday_ooty_first',           label: '2026 · Holiday recommendation',       group: 'plan' },
  { rowKey: 'history_restaurant_visits',    path: 'history_2025.restaurant_visits',                label: '2025 · Restaurants visited',          group: 'history' },
  { rowKey: 'history_weekend_getaway',      path: 'history_2025.weekend_getaway',                  label: '2025 · Weekend getaway',              group: 'history' },
  { rowKey: 'history_holiday_destination',  path: 'history_2025.holiday_destination',              label: '2025 · Holiday destination',          group: 'history' },
];


export default function AyushEditOptionsModal({ row, onClose, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const init = {};
    FIELDS.forEach((f) => { init[f.rowKey] = row?.[f.rowKey] || ''; });
    setDraft(init);
  }, [row]);

  if (!row) return null;

  const saveAll = async () => {
    const body = {};
    let changed = 0;
    FIELDS.forEach((f) => {
      const before = (row[f.rowKey] || '').trim();
      const after = (draft[f.rowKey] || '').trim();
      if (after !== before) {
        body[f.path] = after;
        changed += 1;
      }
    });
    if (changed === 0) {
      toast.info('No changes');
      onClose();
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/xiaomi/dealers/${encodeURIComponent(row.retailer_id)}`,
        body,
        authHdr(),
      );
      toast.success(`Saved · ${changed} field${changed === 1 ? '' : 's'} · audit logged`);
      onSaved?.(draft);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ayush-edit-options-modal"
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b sticky top-0 bg-white" style={{ borderColor: '#e5e1dc' }}>
          <div className="flex justify-between items-start">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-soft">Edit pre-curated options</div>
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>
                {row.outlet_name}
              </h2>
              <div className="text-[12px] text-soft mt-0.5">
                {row.salutation} {row.owner_first_name} · {row.retailer_id} · {row.tier}
              </div>
            </div>
            <button onClick={onClose} className="text-soft hover:text-ink p-1"
              data-testid="ayush-edit-options-close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          <Section title="2026 · plan (sent in Ayush's WhatsApp)" tone="#9A4B0E">
            {FIELDS.filter((f) => f.group === 'plan').map((f) => (
              <Field key={f.rowKey} field={f} draft={draft} setDraft={setDraft} />
            ))}
          </Section>

          <Section title="2025 · history (so we don't repeat last year)" tone="#3730A3">
            {FIELDS.filter((f) => f.group === 'history').map((f) => (
              <Field key={f.rowKey} field={f} draft={draft} setDraft={setDraft} />
            ))}
          </Section>

          <div className="text-[11px] text-soft italic pt-2">
            Saving here updates <code>xiaomi_dealers.preferences_2026.*</code> and
            <code> .history_2025.*</code> in MongoDB and writes one audit log entry
            per changed field. The Compose modal will pick the new values up
            automatically next time you open it.
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t sticky bottom-0 bg-white flex gap-2 justify-end" style={{ borderColor: '#e5e1dc' }}>
          <button onClick={onClose} disabled={saving}
            className="px-3 py-1.5 rounded text-sm border bg-white hover:bg-[#f3f4f6]"
            data-testid="ayush-edit-options-cancel">
            Cancel
          </button>
          <button onClick={saveAll} disabled={saving}
            className="px-4 py-1.5 rounded text-sm text-white font-semibold disabled:opacity-50"
            style={{ background: '#16a34a' }}
            data-testid="ayush-edit-options-save">
            {saving ? 'Saving…' : '✓ Save all (audit-logged)'}
          </button>
        </div>
      </div>
    </div>
  );
}


function Section({ title, tone, children }) {
  return (
    <div className="space-y-3">
      <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: tone }}>
        {title}
      </div>
      {children}
    </div>
  );
}


function Field({ field, draft, setDraft }) {
  return (
    <label className="block" data-testid={`ayush-edit-${field.rowKey}`}>
      <div
        className="text-[11px] mb-1"
        style={{ color: field.highlight ? '#9A4B0E' : '#6b7280', fontWeight: field.highlight ? 600 : 400 }}
      >
        {field.label}
      </div>
      <textarea
        value={draft[field.rowKey] || ''}
        onChange={(e) => setDraft({ ...draft, [field.rowKey]: e.target.value })}
        rows={2}
        className="w-full px-3 py-2 border rounded text-[12px] focus:outline-none focus:border-[#ff6900]"
        style={{ borderColor: field.highlight ? '#fde68a' : '#e5e1dc' }}
      />
    </label>
  );
}
